import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { LP_EVENT_DEFAULT_COLORS, resolveLpEventContentColors } from 'src/modules/lp-events/config/lp-event-theme';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { LpEvent, LpEventCloneDraft } from 'src/types/lp-events';
import { useMyDataStore } from 'src/store/me/me.store';
import { CLONE_FIELD_LABELS, cloneDay, clonePickerDay, pendingCloneFields } from './lp-event-clone.utils';
import useCloneLeaveGuard from './use-clone-leave-guard';
import * as Yup from 'yup';

export default function useLpEventsDetails() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const isClone = pathname.includes('/copy/');
  const [cloneDraft, setCloneDraft] = useState<LpEventCloneDraft | null>(null);
  const [isPreparingClone, setIsPreparingClone] = useState(isClone);
  const [cloneError, setCloneError] = useState('');
  const cloneSaved = useRef(false);
  const me = useMyDataStore((state) => state.me);
  const canClone = !!me?.roles.includes('cn_admin') && !!me?.cnAdminInfo?.permissions?.benefits?.create;

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const { enqueueSnackbar } = useSnackbar();

  const {
    getBusinessUnits,
    getLpEventDetails,
    resetLpEventDetails,
    createLpEvent,
    updateLpEvent,
    prepareClone,
    saveClone,
    isLoadingLpEventDetails,
    businessUnits,
    lpEventDetails: savedLpEventDetails,
  } = useLpEventsStore();
  const lpEventDetails = isClone ? cloneDraft?.event : savedLpEventDetails;

  const { id } = useParams();

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        await getBusinessUnits();
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };

    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    let active = true;
    cloneSaved.current = false;
    const fetchDetails = async () => {
      if (isClone) {
        setIsPreparingClone(true);
        setCloneDraft(null);
        setCloneError('');
        try {
          if (!canClone || !id) return;
          const draft = await prepareClone(id);
          if (active) setCloneDraft(draft);
        } catch (error) {
          if (active) setCloneError(error.message || 'Não foi possível preparar a cópia do evento.');
        } finally {
          if (active) setIsPreparingClone(false);
        }
      } else if (id) {
        await getLpEventDetails(id);
      } else {
        resetLpEventDetails();
      }
    };

    fetchDetails();
    return () => { active = false; };
  }, [id, isClone, canClone, prepareClone, getLpEventDetails, resetLpEventDetails]);

  const pickerDate = (date: Date | string | undefined) => {
    if (!date) return new Date();
    return isClone ? clonePickerDay(String(date)) : new Date(date);
  };
  const defaultValues = {
    name: lpEventDetails?.name || '',
    detail: lpEventDetails?.detail || '',
    URLName: lpEventDetails?.URLName || '',
    startDate: pickerDate(lpEventDetails?.startDate),
    endDate: pickerDate(lpEventDetails?.endDate),
    businessUnitId: lpEventDetails?.businessUnitId || '',
    GAPropertyId: isClone ? '' : lpEventDetails?.GAPropertyId || '',
    GAPropertySecret: isClone ? '' : lpEventDetails?.GAPropertySecret || '',
    customContent: resolveLpEventContentColors(
      lpEventDetails?.customContent || {
        mainBanners: { ...LP_EVENT_DEFAULT_COLORS.mainBanners },
        primaryButtons: { ...LP_EVENT_DEFAULT_COLORS.primaryButtons },
        loginPage: {
          bannerImage: lpEventDetails?.customContent?.loginPage?.bannerImage || '',
          contentImage: lpEventDetails?.customContent?.loginPage?.contentImage || '',
          bannerText:
            lpEventDetails?.customContent?.loginPage?.bannerText || 'Resgate benefícios exclusivos!',
          contentText: {
            primaryText:
              lpEventDetails?.customContent?.loginPage?.contentText?.primaryText ||
              'Viva experiências únicas com a Cidade Center Norte',
            secondaryText:
              lpEventDetails?.customContent?.loginPage?.contentText?.secondaryText ||
              'Aproveite todas as vantagens da campanha de inverno.',
          },
          cardsText: {
            upperLeft:
              lpEventDetails?.customContent?.loginPage?.cardsText?.upperLeft ||
              'Cadastro fácil e rápido!',
            upperRight:
              lpEventDetails?.customContent?.loginPage?.cardsText?.upperRight ||
              'Use seus benefícios agora mesmo!',
            bottomLeft:
              lpEventDetails?.customContent?.loginPage?.cardsText?.bottomLeft ||
              'Cupons e descontos imperdíveis.',
            bottomRight:
              lpEventDetails?.customContent?.loginPage?.cardsText?.bottomRight ||
              'Somente nas lojas participantes!',
          },
        },
        registerPage: {
          bannerImage: lpEventDetails?.customContent?.registerPage?.bannerImage || '',
          contentImage: lpEventDetails?.customContent?.registerPage?.contentImage || '',
          bannerText:
            lpEventDetails?.customContent?.registerPage?.bannerText ||
            'Seja bem-vindo(a)! Cadastre-se e aproveite.',
          contentText: {
            primaryText:
              lpEventDetails?.customContent?.registerPage?.contentText?.primaryText ||
              'Sua experiência começa aqui',
            secondaryText:
              lpEventDetails?.customContent?.registerPage?.contentText?.secondaryText ||
              'Preencha seus dados e aproveite os benefícios.',
          },
          cardsText: {
            upperLeft:
              lpEventDetails?.customContent?.registerPage?.cardsText?.upperLeft ||
              'Cadastro simples e gratuito!',
            upperRight:
              lpEventDetails?.customContent?.registerPage?.cardsText?.upperRight ||
              'Resgate seus itens preferidos!',
            bottomLeft:
              lpEventDetails?.customContent?.registerPage?.cardsText?.bottomLeft ||
              'Descontos nas melhores lojas.',
            bottomRight:
              lpEventDetails?.customContent?.registerPage?.cardsText?.bottomRight ||
              'Só na Cidade Center Norte!',
          },
        },
        homePage: {
          bannerImage: lpEventDetails?.customContent?.homePage?.bannerImage || '',
          bannerText: {
            specialColor: LP_EVENT_DEFAULT_COLORS.homePage.bannerText.specialColor,
            titleText:
              lpEventDetails?.customContent?.homePage?.bannerText?.titleText ||
              'Seus benefícios estão aqui!',
            items: {
              item1: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item1 || {
                cardText: 'ESCOLHA',
                text: 'Navegue pelo catálogo e escolha seus itens.',
              },
              item2: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item2 || {
                cardText: 'RESGATE',
                text: 'Finalize os resgates e acompanhe em Meus Benefícios.',
              },
              item3: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item3 || {
                cardText: 'APROVEITE',
                text: 'Use seus cupons e viva experiências incríveis.',
              },
            },
            infoText:
              lpEventDetails?.customContent?.homePage?.bannerText?.infoText ||
              'Ofertas exclusivas para clientes cadastrados. Promoções válidas enquanto durarem os estoques.',
          },
          contentText: {
            primaryText:
              lpEventDetails?.customContent?.homePage?.contentText?.primaryText ||
              'Tudo que você precisa em um só lugar',
            secondaryText:
              lpEventDetails?.customContent?.homePage?.contentText?.secondaryText ||
              'Benefícios, descontos, cupons e muito mais esperando por você.',
          },
        },
        myBenefitsPage: {
          titleText:
            lpEventDetails?.customContent?.myBenefitsPage?.titleText ||
            'Confira seus resgates e aproveite!',
        },
      },
      isEdit ? 'edit' : 'create'
    ),
    status: lpEventDetails?.status || 'active',
  };

  const lpEventSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    detail: Yup.string().trim().required('Campo obrigatório!'),
    URLName: Yup.string().required('Campo obrigatório!').test('clone-slug', 'Use apenas letras, números e hífens entre palavras.', (value) => !isClone || /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(value ?? '')),
    startDate: Yup.date().required('Campo obrigatório!').nullable(),
    endDate: Yup.date()
      .required('Campo obrigatório!')
      .nullable()
      .min(Yup.ref('startDate'), 'A data de fim deve ser posterior à data de início'),
    businessUnitId: Yup.string().required('Campo obrigatório!'),
    GAPropertyId: Yup.string().required('Campo obrigatório!'),
    GAPropertySecret: Yup.string().when([], {
      is: () => !isEdit, // se NÃO está editando, é obrigatório
      then: (schema) => schema.required('Campo obrigatório!'),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: Yup.string()
      .oneOf(['active', 'inactive'], 'Status inválido')
      .required('Campo obrigatório!'),
    // Preserve legacy layouts as returned by the server. Cloning must not force
    // operators to invent content for optional sections of an older event.
    customContent: isClone ? Yup.mixed<LpEvent['customContent']>().required() : Yup.object().shape({
      loginPage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        contentImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.string().required('Texto obrigatório'),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
        cardsText: Yup.object().shape({
          upperLeft: Yup.string().required('Texto obrigatório'),
          upperRight: Yup.string().required('Texto obrigatório'),
          bottomLeft: Yup.string().required('Texto obrigatório'),
          bottomRight: Yup.string().required('Texto obrigatório'),
        }),
      }),
      registerPage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        contentImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.string().required('Texto obrigatório'),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
        cardsText: Yup.object().shape({
          upperLeft: Yup.string().required('Texto obrigatório'),
          upperRight: Yup.string().required('Texto obrigatório'),
          bottomLeft: Yup.string().required('Texto obrigatório'),
          bottomRight: Yup.string().required('Texto obrigatório'),
        }),
      }),
      homePage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.object().shape({
          titleText: Yup.string().required('Texto obrigatório'),
          items: Yup.object().shape({
            item1: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
            item2: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
            item3: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
          }),
          infoText: Yup.string().required('Texto obrigatório'),
        }),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
      }),
      myBenefitsPage: Yup.object().shape({
        titleText: Yup.string().required('Título obrigatório'),
      }),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(lpEventSchema.test('clone-required-changes', function validateCloneChanges(values) {
      if (!isClone || !cloneDraft) return true;
      const pending = pendingCloneFields(values, cloneDraft.event);
      if (!pending.length) return true;
      throw new Yup.ValidationError(pending.map((field) => this.createError({
        path: field,
        message: `Altere ${CLONE_FIELD_LABELS[field]} para esta nova edição do evento.`,
      })));
    })),
    defaultValues,
    mode: isClone ? 'onChange' : 'onSubmit',
  });

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    control,
    formState: { isSubmitting },
  } = methods;

  const formValues = watch();
  const pendingFields = cloneDraft ? pendingCloneFields(formValues, cloneDraft.event) : [];

  const { isLeaveDialogOpen, handleBack, cancelLeave, confirmLeave } = useCloneLeaveGuard({
    enabled: isClone && !!cloneDraft,
    isSaving: isSubmitting,
    savedRef: cloneSaved,
    backPath: paths.dashboard.lpEventsList,
    onDiscard: () => setCloneDraft(null),
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [reset, lpEventDetails]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isClone) {
        if (!canClone || !cloneDraft || pendingFields.length) return;
        await saveClone({
          sourceEventId: cloneDraft.sourceEventId,
          sourceRevision: cloneDraft.sourceRevision,
          event: {
            ...data,
            startDate: cloneDay(data.startDate),
            endDate: cloneDay(data.endDate),
          } as Parameters<typeof saveClone>[0]['event'],
        });
        cloneSaved.current = true;
        setCloneDraft(null);
        enqueueSnackbar({ message: 'Evento copiado com seus benefícios e banners com sucesso.', variant: 'success' });
        navigate(paths.dashboard.lpEventsList);
        return;
      }
      const formattedData = {
        ...data,
        _id: id,
      };

      if (isEdit && !formattedData.GAPropertySecret) {
        delete formattedData.GAPropertySecret;
      }

      if (isEdit && id) {
        await updateLpEvent(id, formattedData as LpEvent);
        enqueueSnackbar({ message: 'Evento editado com sucesso.', variant: 'success' });
      } else {
        await createLpEvent(data as LpEvent);
        enqueueSnackbar({ message: 'Evento criado com sucesso.', variant: 'success' });
      }
      navigate(paths.dashboard.lpEventsList);
    } catch (error) {
      if (isClone && /URL/.test(error.message)) setError('URLName', { message: error.message });
      if (isClone && /Analytics/.test(error.message)) setError('GAPropertyId', { message: error.message });
      enqueueSnackbar({ message: error.message, variant: 'error' });
    }
  });

  const handleDropBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.loginPage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropContentImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.loginPage.contentImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropRegisterPageBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.registerPage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropRegisterPageContentImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.registerPage.contentImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropHomePageBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.homePage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  return {
    isClone,
    canClone,
    cloneDraft,
    cloneError,
    isLeaveDialogOpen,
    handleBack,
    cancelLeave,
    confirmLeave,
    pendingFields,
    formValues,
    isEdit,
    isLoadingLpEventDetails: isClone ? isPreparingClone : isLoadingLpEventDetails,
    isSubmitting,
    control,
    methods,
    businessUnits,
    lpEventDetails,
    STATUS_OPTIONS,
    STATUS_LABEL,
    handleDropBannerImage,
    handleDropContentImage,
    handleDropRegisterPageBannerImage,
    handleDropRegisterPageContentImage,
    handleDropHomePageBannerImage,
    navigate,
    onSubmit,
  };
}
